import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api, filterParams, filterKey } from "@/lib/api";
import { fmtCompact } from "@/lib/format";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

const COLORS = { input: "#3B82F6", output: "#F59E0B", cache: "#71717A" };

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-black border border-border p-3 font-mono text-xs">
      <p className="text-white mb-2">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.color }}>{p.dataKey}: {fmtCompact(p.value)}</p>
      ))}
    </div>
  );
}

export default function TokenBreakdownChart({ filters }) {
  const { data = [] } = useQuery({
    queryKey: ["token-breakdown", ...filterKey(filters)],
    queryFn: async () => (await api.get("/token-breakdown", { params: filterParams(filters) })).data,
    refetchInterval: 20000,
  });

  return (
    <div className="bg-card border border-border p-5" data-testid="token-breakdown-chart">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="overline">token breakdown</p>
          <h3 className="text-sm font-medium mt-1">Input · Output · Cache</h3>
        </div>
        <div className="flex gap-3">
          {Object.entries(COLORS).map(([k, c]) => (
            <span key={k} className="flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground">
              <span className="w-2.5 h-2.5" style={{ background: c }} />{k}
            </span>
          ))}
        </div>
      </div>
      <div className="h-[260px]">
        {data.length === 0 ? (
          <div className="h-full flex items-center justify-center font-mono text-xs text-muted-foreground">no data in range</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="0" stroke="#1c1c22" vertical={false} />
              <XAxis dataKey="bucket" stroke="#52525b" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} tickFormatter={(v) => v.slice(5)} />
              <YAxis stroke="#52525b" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} tickFormatter={fmtCompact} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
              <Bar dataKey="input" stackId="a" fill={COLORS.input} />
              <Bar dataKey="output" stackId="a" fill={COLORS.output} />
              <Bar dataKey="cache" stackId="a" fill={COLORS.cache} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
